import { Box, Alert, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { AdminOfflineGuard } from '@components/guards/AdminOfflineGuard';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useAcknowledgmentConfigData } from './hooks';
import {
  AcknowledgmentConfigHeader,
  AcknowledgmentConfigContent
} from './components';

const DEFAULT_STATEMENT =
  'I acknowledge that I have reviewed the calibration results and findings presented above. I understand that this review represents the current state of the equipment as tested.';

export const CustomerAcknowledgmentConfig = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();

  const {
    customStatement,
    isLoading,
    error,
    hasUnsavedChanges,
    isSaving,
    saveError,
    updateStatement,
    clearCustomStatement,
    saveChanges,
    discardChanges
  } = useAcknowledgmentConfigData();

  const handleSave = async () => {
    await saveChanges();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isOnline) {
    return <AdminOfflineGuard children={null} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t(
            'settings:customerAcknowledgment.errors.loadFailed',
            'Failed to load customer acknowledgment configuration'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: theme.palette.background.default
      }}
    >
      <AcknowledgmentConfigHeader
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onSave={handleSave}
        onDiscard={discardChanges}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0
        }}
      >
        {saveError && (
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2 }}>
            <Alert severity="error">
              {t(
                'settings:customerAcknowledgment.errors.saveFailed',
                'Failed to save configuration'
              )}
              : {saveError.message}
            </Alert>
          </Box>
        )}

        <AcknowledgmentConfigContent
          customStatement={customStatement}
          defaultStatement={DEFAULT_STATEMENT}
          onUpdateStatement={updateStatement}
          onClearStatement={clearCustomStatement}
        />
      </Box>
    </Box>
  );
};
